import type { TemplateInput } from '@amzn/mulberry-internal-typescript-client';
import {DEV_CLOUDFRONT_URL, LOCAL_HOSTNAME, STAGE_PROD} from 'src/constants/appConstants';
import {isLocalHost, isPreviewHost} from "src/utils/utils";
import { fetchTemplateFromPreviewAPI } from 'src/utils/previewAuth';
import { FONT_FAMILY_MAP } from '@amzn/burberry-ui-components';

export interface SiteManifest {
  configUrl: string;
  tosPage?: string;
  faqPage?: string;
  ppPage?: string;
}

export const CUSTOM_PAGE_MANIFEST_KEY: Record<string, keyof SiteManifest> = {
  faq: 'faqPage',
  tos: 'tosPage',
  pp: 'ppPage',
};

export const getBaseUrl = (): string => {
  // Local development: use the dev CloudFront URL since template files
  // don't exist on localhost. Matches localhost, 0.0.0.0, 127.0.0.1,
  // or the custom hostname mapped via /etc/hosts (from .env LOCAL_HOSTNAME).
  if (isLocalHost()) {
    return DEV_CLOUDFRONT_URL;
  }
  return '';
};

/**
 * Fetches the site template for the given site ID.
 * - PREVIEW mode (version specified): calls Mulberry internal API to get draft from DDB.
 * - LIVE mode: fetches manifest pointer + config file from S3 via CloudFront.
 */
export interface FetchSiteConfigResult {
  template: TemplateInput;
  manifest?: SiteManifest;
}

export const fetchSiteConfig = async (siteId: string, version: string, isPreview?: boolean): Promise<FetchSiteConfigResult> => {
  // Only allow API calls to the Mulberry internal service from the preview distribution
  // AND when preview mode is explicitly requested via ?preview=true.
  // Prevents API calls when developing the live site locally without preview intent.
  if (isPreviewHost() && isPreview) {
    try {
      const template = await fetchTemplateFromPreviewAPI(siteId, version);
      return { template };
    } catch (error: any) {
      if (error.message === 'Redirecting to Midway for authentication') {
        return new Promise(() => {});
      }
      if (isLocalHost()) {
        console.warn('Preview API call failed locally, falling back to S3:', error);
      } else {
        throw new Error(`Failed to fetch template for site: ${siteId} and Version ${version}`);
      }
    }
  }

  const baseUrl = getBaseUrl();
  const manifestPath = `${baseUrl}/sites/${siteId}/${siteId}-manifest-pointer-file`;

  const manifestResponse = await fetch(manifestPath);
  if (!manifestResponse.ok) {
    throw new Error(`Failed to fetch manifest for site: ${siteId}`);
  }
  const manifest = await manifestResponse.json() as SiteManifest;

  const configUrl = manifest.configUrl.startsWith('http')
    ? manifest.configUrl
    : `${baseUrl}/${manifest.configUrl}`;

  const templateResponse = await fetch(configUrl);
  if (!templateResponse.ok) {
    throw new Error(`Failed to fetch template for site: ${siteId}`);
  }
  const template = await templateResponse.json() as TemplateInput;
  return { template, manifest };
};

// Returns the site ID. In prod/local, uses the hostname directly.
// On non-prod deployed stacks, reads from ?site= param and persists to sessionStorage
// (per-tab) so it survives internal navigation and page reloads without cross-tab interference.
export const getSite = (stage: string, env: string): string => {
  if ((stage === STAGE_PROD && env === 'LIVE') || window.location.hostname === LOCAL_HOSTNAME) {
    return window.location.hostname;
  }
  const searchParams = new URLSearchParams(window.location.search);
  const siteFromUrl = searchParams.get('site');
  if (siteFromUrl) {
    sessionStorage.setItem('burberrySiteId', siteFromUrl);
    return siteFromUrl;
  }
  return sessionStorage.getItem('burberrySiteId') ?? '';
};

// Determines if the app is in preview mode.
// ?preview=true → stores and returns true. ?preview=false → explicitly clears storage.
// No params (internal navigation/reload) → falls back to sessionStorage.
export const getIsPreview = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const previewParam = searchParams.get('preview');
  if (previewParam === 'true') {
    sessionStorage.setItem('burberryPreview', 'true');
  } else if (previewParam === 'false') {
    sessionStorage.removeItem('burberryPreview');
    sessionStorage.removeItem('burberryVersion');
  }
  return previewParam === 'true' || sessionStorage.getItem('burberryPreview') === 'true';
};

// Returns the template version. In prod live, always 'LIVE'.
// Only stores version in sessionStorage when in preview mode.
// ?site= without ?version= in preview → resets to 'LIVE'. No params → falls back to stored value.
export const getVersion = (stage: string, env: string, isPreview: boolean): string => {
  if (stage === STAGE_PROD && env === 'LIVE') {
    return 'LIVE';
  }
  const searchParams = new URLSearchParams(window.location.search);
  const versionFromUrl = searchParams.get('version');

  if (versionFromUrl && isPreview) {
    sessionStorage.setItem('burberryVersion', versionFromUrl);
    return versionFromUrl;
  }
  if (searchParams.has('site') && isPreview) {
    sessionStorage.setItem('burberryVersion', 'LIVE');
    return 'LIVE';
  }
  return sessionStorage.getItem('burberryVersion') ?? 'LIVE';
};

export const generateCSSVariables = (siteId: string, siteConfig: TemplateInput): string => {
  const v = (value: string | number | boolean | undefined | null): string =>
    value != null ? String(value) : '';

  // "font" is the primary/stylized font used for headings
  // "secondaryFont" is the readable font for body text; falls back to "font" when not set
  const headingFont = siteConfig.font ? (FONT_FAMILY_MAP[siteConfig.font] || siteConfig.font) : '';
  const secondaryFont = siteConfig.secondaryFont ? (FONT_FAMILY_MAP[siteConfig.secondaryFont] || siteConfig.secondaryFont) : '';
  const buttonFont = siteConfig.buttonFont ? (FONT_FAMILY_MAP[siteConfig.buttonFont] || siteConfig.buttonFont) : '';
  const linkFont = siteConfig.linkFont ? (FONT_FAMILY_MAP[siteConfig.linkFont] || siteConfig.linkFont) : '';

  return `
    [site-id="${siteId}"] {
      --background-color: ${v(siteConfig.backgroundColor)};
      --background-image-url: ${siteConfig.backgroundImageUrl ? `url(${siteConfig.backgroundImageUrl})` : 'none'};
      --primary-color: ${v(siteConfig.primaryColor)};
      --secondary-color: ${v(siteConfig.secondaryColor)};
      --accent-color: ${v(siteConfig.accentColor)};
      --button-color: ${v(siteConfig.buttonColor)};
      --button-hover-color: ${v(siteConfig.buttonHoverColor)};
      --font-family: ${headingFont};
      --heading-font-family: ${headingFont};
      --secondary-font-family: ${secondaryFont || headingFont};
      --body-font-family: ${secondaryFont || headingFont};
      --button-font-family: ${buttonFont || secondaryFont || headingFont};
      --link-font-family: ${linkFont || secondaryFont || headingFont};
      --h1-font-size: ${v(siteConfig.h1FontSize)};
      --h2-font-size: ${v(siteConfig.h2FontSize)};
      --h3-font-size: ${v(siteConfig.h3FontSize)};
      --paragraph-font-size: ${v(siteConfig.paragraphFontSize)};
      --header-font-weight: ${v(siteConfig.headerFontWeight)};
      --paragraph-font-weight: ${v(siteConfig.paragraphFontWeight)};
      --text-transform: ${v(siteConfig.textTransform)};
      --nav-bar-height: ${v(siteConfig.navBarHeight)};
      --nav-bar-font-color: ${v(siteConfig.navBarFontColor)};
      --footer-height: ${v(siteConfig.footerHeight)};
      --footer-font-size: ${v(siteConfig.footerFontSize)};
      --footer-font-color: ${v(siteConfig.footerFontColor)};
      --merch-grid-columns: ${v(siteConfig.merchGridColumns)};
      --merch-grid-spacing: ${v(siteConfig.merchGridSpacing)};
      --top-promo-banner-background-color: ${v(siteConfig.topPromoBannerBackgroundColor)};
      --top-promo-banner-background-image: ${v(siteConfig.topPromoBannerBackgroundImage)};
      --top-promo-banner-height: ${v(siteConfig.topPromoBannerHeight)};
      --footer-banner-image-url: ${v(siteConfig.footerBanner?.bannerImageUrl)};
      --site-max-width: 1920px;
      --center-or-full-width: ${v(siteConfig.centerOrFullWidth)};
    }
  `;
};
